import { useRef } from "react";

/**
 * Consume a `?note=`/`?clip=`/`?goal=`/`?summary=` deep-link exactly
 * once per DISTINCT value.
 *
 * The problem this solves: the Review tab used to read its deep-link
 * through a lazy `useState` initializer, which runs only on first
 * mount. That was deliberate — it stops a re-render fighting a user
 * who has since clicked a different filter chip — but it also made a
 * bell link inert whenever the tab was already open, which is the
 * common case for anyone reading their feedback.
 *
 * Reacting to the param on every render would fix the bell and
 * reintroduce the original bug: a background refetch could yank the
 * reader back to the deep-linked item minutes later.
 *
 * So: react to a value only the first time it is seen. A fresh bell
 * click (a value we have not consumed) applies; the same value
 * arriving again on any subsequent render does not. Clicking the bell
 * twice for one item is a no-op; clicking it for a different item
 * jumps there.
 *
 * Returns the value when it is newly arrived, `null` otherwise — so a
 * caller can drive `useEffect` on it without re-applying stale hints.
 */
export function useDeepLinkTarget<T extends string | number>(
  value: T | null | undefined,
): T | null {
  // `undefined` marks "nothing consumed yet", distinct from a real
  // `null` param, so the very first non-null value still counts as new.
  const consumed = useRef<T | null | undefined>(undefined);

  const incoming = value ?? null;
  if (incoming !== null && consumed.current !== incoming) {
    // Ref assignment during render is safe: no state update, and the
    // value returned below reflects it in this same render.
    consumed.current = incoming;
    return incoming;
  }
  // Clearing the param (navigating away in-tab) re-arms the same id, so
  // returning to it later counts as new again.
  if (incoming === null) consumed.current = null;
  return null;
}
